// Hooks over the tenant half of the prompt contract. Keyed under
// ["prompts", "tenant", tenantId, ...] — no collision with the platform keys,
// whose second segment is always a dotted touchpoint slug.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LlmParams, LlmTouchpointSlug } from "@gb/admin-contracts";
import { api, errorMessage } from "../../api/client";

export const tenantPromptKeys = {
  all: (tenantId: string) => ["prompts", "tenant", tenantId] as const,
  detail: (tenantId: string, slug: string) => [...tenantPromptKeys.all(tenantId), slug] as const,
  versions: (tenantId: string, slug: string) =>
    [...tenantPromptKeys.all(tenantId), slug, "versions"] as const,
};

export function useTenantTouchpoints(tenantId: string) {
  return useQuery({
    queryKey: tenantPromptKeys.all(tenantId),
    queryFn: async () => {
      const res = await api.prompts.tenant.listTouchpoints({ params: { tenantId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.touchpoints;
    },
  });
}

export function useTenantTouchpoint(tenantId: string, slug: LlmTouchpointSlug) {
  return useQuery({
    queryKey: tenantPromptKeys.detail(tenantId, slug),
    queryFn: async () => {
      const res = await api.prompts.tenant.getTouchpoint({ params: { tenantId, slug } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useTenantVersions(tenantId: string, slug: LlmTouchpointSlug) {
  return useQuery({
    queryKey: tenantPromptKeys.versions(tenantId, slug),
    queryFn: async () => {
      const res = await api.prompts.tenant.listVersions({ params: { tenantId, slug } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.versions;
    },
  });
}

export function useCreateTenantVersion(tenantId: string, slug: LlmTouchpointSlug) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; params: LlmParams; note?: string }) => {
      const res = await api.prompts.tenant.createVersion({
        params: { tenantId, slug },
        body: input,
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.version;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: tenantPromptKeys.all(tenantId) }),
  });
}

export function useActivateTenantVersion(tenantId: string, slug: LlmTouchpointSlug) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string | null) => {
      const res = await api.prompts.tenant.activate({
        params: { tenantId, slug },
        body: { versionId },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: tenantPromptKeys.all(tenantId) }),
  });
}
